import { useEffect, useRef } from 'react';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { useSignal } from '@backstage/plugin-signals-react';
import { getPollIntervalMs } from '../lib/dashboardPollInterval';

// Placeholder channel when the caller doesn't yet know its real channel
// (e.g. requestId not resolved yet) — useSignal() requires a channel
// string; this one is never published to, so it's a safe no-op.
const NO_CHANNEL = 'vault:__no-channel__';

// Shared by the estate dashboard and the 3 entity cards: push via signals
// as the primary update path, with a config-driven poll fallback for a
// dropped/reconnecting WebSocket connection. See publishRequestSignals.ts
// (vault-backend) for what publishes to these channels and why.
export function useLiveUpdates(
  channel: string | undefined,
  onUpdate: () => void,
): void {
  const config = useApi(configApiRef);
  const { lastSignal } = useSignal(channel ?? NO_CHANNEL);
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  const isFirstSignal = useRef(true);
  useEffect(() => {
    if (isFirstSignal.current) {
      isFirstSignal.current = false;
      return;
    }
    onUpdateRef.current();
  }, [lastSignal]);

  useEffect(() => {
    if (!channel) return undefined;
    const intervalMs = getPollIntervalMs(
      config,
      'vault.dashboard.pollIntervalMs',
      60_000,
    );
    const interval = setInterval(() => onUpdateRef.current(), intervalMs);
    return () => clearInterval(interval);
  }, [channel, config]);
}
